import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, ServiceBar } from "@/components/Logo";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

export default function SetupPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres");
    if (password !== confirm) return setError("Las contraseñas no coinciden");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/setup-password", { token, password });
      localStorage.setItem("pe_token", r.data.access_token);
      localStorage.setItem("pe_user", JSON.stringify(r.data.user));
      setOk(true);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--pe-bg)] p-6">
        <div className="pe-card p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
          <h2 className="text-2xl font-black text-[color:var(--pe-primary)]">Link inválido</h2>
          <p className="text-slate-600 mt-2">
            El link de invitación no contiene un token. Solicita uno nuevo al administrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--pe-bg)]">
      <div className="bg-white px-6 py-5 flex items-center justify-between border-b border-[color:var(--pe-border)]">
        <Logo height={44} />
        <span className="text-[10px] font-bold tracking-widest text-[color:var(--pe-primary)] hidden sm:block">
          SOLUCIONES DE VANGUARDIA
        </span>
      </div>
      <ServiceBar />
      <div className="max-w-md mx-auto px-6 py-14">
        <div className="pe-label mb-2">ACTIVA TU CUENTA</div>
        <h1 className="text-3xl font-black text-[color:var(--pe-primary)]">Establece tu contraseña</h1>
        <p className="text-slate-500 mt-2">Solo tú conocerás esta contraseña. El administrador no la puede ver.</p>
        {ok ? (
          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-md p-4 flex gap-3 items-start">
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={22} />
            <div>
              <div className="font-bold text-emerald-800">Contraseña establecida</div>
              <div className="text-sm text-emerald-700">Iniciando sesión…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label className="pe-label">Nueva contraseña</Label>
              <Input type="password" required className="h-12" value={password}
                     onChange={(e) => setPassword(e.target.value)} data-testid="setup-password-input" />
              <p className="text-xs text-slate-500">Mínimo 8 caracteres.</p>
            </div>
            <div className="space-y-2">
              <Label className="pe-label">Confirmar contraseña</Label>
              <Input type="password" required className="h-12" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} data-testid="setup-password-confirm" />
            </div>
            {error && (
              <div className="flex gap-2 items-start text-sm bg-red-50 text-red-800 border border-red-200 rounded-md p-3">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" disabled={submitting}
                    className="w-full h-12 pe-btn-primary text-white text-base"
                    data-testid="setup-submit-button">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : "Establecer y entrar"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
